import { Badge } from "@/components/ui/badge";
import { Calendar, Users } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Campaign } from "@/types/campaign";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n/labels/adminLabels";
import {
  getTypeColor,
  getTypeLabel,
  getStatusColor,
  getStatusLabel,
  getBorderColor,
  getCampaignStatus,
} from "@/utils/listingUtils";

interface CampaignCardProps {
  campaign: Campaign;
  /** Omit for view-only mode (no drill-in to details). */
  onCampaignClick?: (campaign: Campaign) => void;
  vehicleCount?: number;
}

export const CampaignCard = ({
  campaign,
  onCampaignClick,
  vehicleCount = 0,
}: CampaignCardProps) => {
  const { language } = useLanguage();
  const L =
    (adminLabels(language) as { leadboxListings?: Record<string, string> })
      ?.leadboxListings ?? {};
  const statusToDisplay = getCampaignStatus(campaign);
  const campaignDateLocale = language === "en" ? undefined : sv;

  return (
    <div
      className={`group relative rounded-lg bg-card border border border-b-2 ${getBorderColor(
        campaign.type,
      )} shadow-sm hover:shadow-md transition-all overflow-hidden ${
        onCampaignClick ? "cursor-pointer" : "cursor-default"
      }`}
      onClick={onCampaignClick ? () => onCampaignClick(campaign) : undefined}
    >
      <div className="h-32 overflow-hidden relative">
        {campaign.thumbnailImage ? (
          <img
            src={campaign.thumbnailImage.url}
            alt={campaign.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <span className="text-gray-500 text-sm">
              {L.noImage ?? "Ingen bild"}
            </span>
          </div>
        )}

        {/* Badges on image */}
        <div className="absolute top-2 left-2 flex flex-row gap-1">
          <Badge
            variant="outline"
            className={`text-xs ${getTypeColor(campaign.type)}`}
          >
            {getTypeLabel(campaign.type, language)}
          </Badge>
          <Badge
            variant="outline"
            className={`text-xs ${getStatusColor(statusToDisplay)}`}
          >
            {getStatusLabel(statusToDisplay, language)}
          </Badge>
        </div>
      </div>

      <div className="p-3 space-y-2">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm truncate">{campaign.name}</h3>
            <p className="text-xs text-primary/60 line-clamp-2 mt-1">
              {campaign.description}
            </p>

            {/* Target User Type Label */}
            {campaign.targetUserType !== "all" && (
              <div className="mt-2">
                <Badge
                  variant="outline"
                  className={`text-xs ${
                    campaign.targetUserType === "dealers-only"
                      ? "dark:border-blue-500 dark:text-blue-200 dark:bg-blue-900/50 border-blue-500 text-blue-600 bg-blue-50"
                      : "dark:border-green-500 dark:text-green-200 dark:bg-green-900/50 border-green-500 text-green-600 bg-green-50"
                  }`}
                >
                  {campaign.targetUserType === "dealers-only"
                    ? "Bilhandlare"
                    : "Privat- & företagskunder"}
                </Badge>
              </div>
            )}

            {/* Labels */}
            {campaign.labels && campaign.labels.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {campaign.labels.map((label, index) => {
                  const labelConfig = {
                    reduced_percentage: {
                      color: "bg-red-100 text-red-800",
                      icon: "%",
                    },
                    reduced_amount: {
                      color: "bg-green-100 text-green-800",
                      icon: "kr",
                    },
                    special: { color: "bg-blue-100 text-blue-800", icon: "★" },
                  }[label.labelType];

                  return (
                    <Badge
                      key={index}
                      variant="secondary"
                      className={`text-xs px-2 py-0.5 ${labelConfig.color}`}
                    >
                      {labelConfig.icon} {label.text}
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Info section */}
        <div className="pt-2 border-t border-primary/40 space-y-2">
          {/* Start date */}
          <div className="flex items-center gap-1 text-xs text-primary/60">
            <Calendar className="h-3 w-3" />
            <span className="font-bold">{L.start ?? "Start"}:</span>
            <span>
              {format(new Date(campaign.startDate), "yyyy-MM-dd HH:mm", {
                locale: campaignDateLocale,
              })}
            </span>
          </div>

          {/* End date */}
          <div className="flex items-center gap-1 text-xs text-primary/60">
            <Calendar className="h-3 w-3" />
            <span className="font-bold">{L.end ?? "Slut"}:</span>
            <span>
              {format(new Date(campaign.endDate), "yyyy-MM-dd HH:mm", {
                locale: campaignDateLocale,
              })}
            </span>
          </div>

          {/* Vehicle count */}
          <div className="flex items-center gap-1 text-xs font-medium text-[#9b87f5]">
            <Users className="h-3 w-3" />
            <span className="font-bold">
              {(
                L.listingsCountShort ??
                L.listingsCount ??
                "{count} annonser"
              ).replace("{count}", String(vehicleCount))}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
